import { gql, useMutation } from '@apollo/client';
import useAppSelector from '../../app/redux/useAppSelector';
import POST_FIELDS_FRAGMENT from '../../graphql/fragments/PostFields';
import type {
  PinPostMutation,
  PinPostMutationVariables,
  TopicPostsOrder,
  TopicPostsType,
  UnpinPostMutation,
  UnpinPostMutationVariables,
  UsePinMutations_TopicFragment,
} from '../../graphql/generated/graphqlTypes';
import topicPolicyCheck, { TopicPolicyAction } from '../topicPolicyCheck';

const PIN_POST_MUTATION = gql`
  mutation pinPost(
    $first: Int!
    $pinnedOrder: Int!
    $orderBy: TopicPostsOrder!
    $postId: ID!
    $topicId: ID!
    $type: TopicPostsType
  ) {
    pinPost(postId: $postId, topicId: $topicId, pinnedOrder: $pinnedOrder) {
      id
      posts(first: $first, orderBy: $orderBy, type: $type) {
        edges {
          node {
            ...PostFields
          }
          pinnedOrder
        }
      }
    }
  }
  ${POST_FIELDS_FRAGMENT}
`;

const UNPIN_POST_MUTATION = gql`
  mutation unpinPost(
    $first: Int!
    $orderBy: TopicPostsOrder!
    $postId: ID!
    $topicId: ID!
    $type: TopicPostsType
  ) {
    unpinPost(postId: $postId, topicId: $topicId) {
      id
      posts(first: $first, orderBy: $orderBy, type: $type) {
        edges {
          node {
            ...PostFields
          }
          pinnedOrder
        }
      }
    }
  }
  ${POST_FIELDS_FRAGMENT}
`;

const TOPIC_FRAGMENT = gql`
  fragment usePinMutations_topic on Topic {
    id
    ...topicPolicyCheck_topic
  }
  ${topicPolicyCheck.fragments.topic}
`;

const usePinMutations = ({
  topic,
  type,
  orderBy,
  postsCount,
}: {
  topic?: UsePinMutations_TopicFragment;
  type: TopicPostsType | null;
  orderBy: TopicPostsOrder;
  postsCount: number;
}) => {
  const currentUser = useAppSelector((state) => state.session.user);

  const canPin = topic
    ? topicPolicyCheck(TopicPolicyAction.EditTopic, topic, currentUser)
    : false;

  const [pinPostMutate] = useMutation<
    PinPostMutation,
    PinPostMutationVariables
  >(PIN_POST_MUTATION);
  const [unpinPostMutate] = useMutation<
    UnpinPostMutation,
    UnpinPostMutationVariables
  >(UNPIN_POST_MUTATION);

  const pinPost =
    topic && canPin
      ? (postId: string, pinnedOrder: number) =>
          pinPostMutate({
            variables: {
              first: postsCount,
              orderBy,
              type,
              topicId: topic.id,
              postId,
              pinnedOrder,
            },
          })
      : null;

  const unpinPost =
    topic && canPin
      ? (postId: string) =>
          unpinPostMutate({
            variables: {
              first: postsCount,
              orderBy,
              type,
              topicId: topic.id,
              postId,
            },
          })
      : null;

  return { pinPost, unpinPost };
};

usePinMutations.fragments = {
  topic: TOPIC_FRAGMENT,
};

export default usePinMutations;
